import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search, User, Wallet, XCircle, Loader2, FileText, Clock, Sparkles,
  AlertTriangle, TrendingUp, ShieldCheck, ShieldAlert, ShieldQuestion,
  ListChecks, Receipt, ClipboardList, CheckCircle2,
  Lock, Unlock, MessageSquare, Upload, Trash2, Plus, CreditCard,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDate, formatDateTime } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'

interface PessoaLista {
  id: number
  codigo?: string | null
  nome: string
  documento?: string | null
  pessoa_tipo?: string | null
  email?: string | null
  telefone?: string | null
  cidade?: string | null
  uf?: string | null
  credito_bloqueado?: boolean
  limite_credito?: number | null
}

interface AnaliseIA {
  nivel_risco: 'baixo' | 'moderado' | 'alto' | 'sem_historico'
  resumo: string
  recomendacao_limite: number
  fator_confianca: number
  ticket_medio: number
  alertas: string[]
  acoes_sugeridas: string[]
  metodo: string
}

interface Dossie {
  pessoa: any
  credito: {
    limite: number | null
    limite_configurado: boolean
    bloqueado: boolean
    nivel_cobranca: string
    condicao_pagamento_id: number | null
    saldo_devedor: number
    disponivel: number | null
    percentual_usado: number | null
    observacoes_financeiras: string | null
    score_serasa: number | null
    ultima_consulta_serasa_em: string | null
  }
  resumo_comportamento: {
    total_titulos_historico: number
    titulos_pagos: number
    titulos_pagos_no_prazo: number
    titulos_pagos_com_atraso: number
    titulos_em_aberto: number
    titulos_em_atraso_agora: number
    atraso_medio_dias: number
    pontualidade_pct: number | null
    total_pedidos_exibidos: number
    total_comprado_exibido: number
    ticket_medio_exibido: number | null
    ultimo_pedido_em: string | null
  }
  analise_ia: AnaliseIA
  titulos_em_aberto: any[]
  ultimos_pedidos: any[]
  ultimos_pagamentos: any[]
}

type TabId = 'titulos' | 'pedidos' | 'pagamentos' | 'documentos' | 'historico'

export default function AnaliseCreditoPage() {
  const [pessoaSel, setPessoaSel] = useState<PessoaLista | null>(null)
  const [busca, setBusca] = useState('')
  const [buscaDeb, setBuscaDeb] = useState('')

  // Debounce: evita uma chamada a cada tecla
  useEffect(() => {
    const t = setTimeout(() => setBuscaDeb(busca.trim()), 250)
    return () => clearTimeout(t)
  }, [busca])

  // Busca SERVER-SIDE: procura em TODOS os clientes (não no cache limitado a 1000)
  const { data: resultados = [], isFetching: buscando } = useQuery<PessoaLista[]>({
    queryKey: ['credito-busca', buscaDeb],
    queryFn: async () => (await api.get('/cadastros/pessoas', {
      params: { tipo: 'cliente', q: buscaDeb }
    })).data,
    enabled: buscaDeb.length >= 2,
  })

  const filtradas = useMemo(() => resultados.slice(0, 50), [resultados])

  // Pendentes de liberação: clientes com credito_bloqueado=true e SEM limite definido.
  // Vem do servidor (mesmo critério do badge) — não depende da lista capada de pessoas.
  const { data: pendentes = [] } = useQuery<PessoaLista[]>({
    queryKey: ['credito-pendentes-lista'],
    queryFn: async () => (await api.get('/financeiro/analise-credito/pendentes')).data,
  })

  const { data: dossie, isLoading } = useQuery<Dossie>({
    queryKey: ['dossie', pessoaSel?.id],
    queryFn: async () =>
      (await api.get(`/financeiro/analise-credito/${pessoaSel!.id}`)).data,
    enabled: !!pessoaSel,
  })

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Análise de Crédito</h1>
        <p className="text-sm text-ink-500">
          Consulte limite, saldo devedor, histórico de pagamentos e documentos do cliente.
        </p>
      </div>

      <div className="card card-body">
        <label className="label">Buscar cliente</label>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            className="input pl-9"
            placeholder="Nome, código ou CPF/CNPJ..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
        </div>
        {busca.trim().length === 1 && (
          <div className="mt-2 text-xs text-ink-500">Digite ao menos 2 letras…</div>
        )}
        {buscaDeb.length >= 2 && (
          <div className="mt-2 border border-ink-200 rounded-md divide-y max-h-64 overflow-auto">
            {buscando && (
              <div className="px-3 py-2 text-xs text-ink-500 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Buscando…
              </div>
            )}
            {!buscando && filtradas.length === 0 && (
              <div className="px-3 py-2 text-xs text-ink-500">Nenhum cliente encontrado.</div>
            )}
            {filtradas.map(p => (
              <button key={p.id} onClick={() => { setPessoaSel(p); setBusca('') }}
                className="w-full text-left px-3 py-2 hover:bg-brand-50 transition">
                <div className="flex items-center gap-2">
                  <User size={14} className="text-ink-400" />
                  <span className="font-medium text-sm">{p.nome}</span>
                  {p.codigo && <span className="text-xs text-ink-500 font-mono">({p.codigo})</span>}
                </div>
                {p.documento && <div className="text-xs text-ink-500 ml-6">{p.documento}</div>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Painel: PENDENTES DE LIBERAÇÃO — sempre visível na home da página */}
      {!pessoaSel && (
        <div className={`card card-body border-2 ${
          pendentes.length > 0 ? 'border-amber-300 bg-amber-50/30' : 'border-emerald-300 bg-emerald-50/30'
        }`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {pendentes.length > 0 ? (
                <ShieldQuestion size={18} className="text-amber-600" />
              ) : (
                <ShieldCheck size={18} className="text-emerald-600" />
              )}
              <h2 className={`font-semibold ${pendentes.length > 0 ? 'text-amber-900' : 'text-emerald-900'}`}>
                Pendentes de liberação
              </h2>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                pendentes.length > 0 ? 'bg-amber-200 text-amber-900' : 'bg-emerald-200 text-emerald-900'
              }`}>
                {pendentes.length}
              </span>
            </div>
            <span className={`text-xs ${pendentes.length > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
              {pendentes.length > 0
                ? 'Cadastros novos aguardando análise — só vendem à vista até liberar.'
                : 'Tudo em dia — nenhum cadastro novo aguardando análise.'}
            </span>
          </div>
          {pendentes.length > 0 ? (
            <div className="border border-amber-200 rounded-md divide-y divide-amber-200 max-h-80 overflow-auto bg-white">
              {pendentes.map(p => (
                <button key={p.id} onClick={() => setPessoaSel(p)}
                  className="w-full text-left px-3 py-2 hover:bg-amber-50 transition flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                    <Clock size={14} className="text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{p.nome}</span>
                      {p.codigo && <span className="text-xs text-ink-500 font-mono shrink-0">#{p.codigo}</span>}
                    </div>
                    <div className="text-xs text-ink-500 truncate">
                      {[p.documento, p.cidade && `${p.cidade}${p.uf ? '/' + p.uf : ''}`, p.telefone]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div className="text-xs text-amber-700 font-medium shrink-0">Revisar →</div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-xs text-emerald-700 italic">
              Quando um cliente novo for cadastrado (no celular ou no painel) ele aparece aqui automaticamente pra você revisar antes de liberar vendas a prazo.
            </p>
          )}
        </div>
      )}

      {!pessoaSel && !busca && (
        <div className="card card-body text-center py-12 text-ink-500">
          <Wallet size={36} className="mx-auto mb-3 text-ink-300" />
          <p className="font-medium">Busque um cliente para ver análise de crédito</p>
          <p className="text-xs mt-1">Use a barra de busca acima</p>
        </div>
      )}

      {pessoaSel && isLoading && (
        <div className="card card-body text-center py-12 text-ink-500">
          <Loader2 size={24} className="mx-auto mb-2 animate-spin" />
          <p className="text-sm">Carregando dossiê...</p>
        </div>
      )}

      {pessoaSel && dossie && (
        <DossieView
          pessoa={pessoaSel}
          dossie={dossie}
          onFechar={() => setPessoaSel(null)}
        />
      )}
    </div>
  )
}

// ============================================================
// DOSSIE VIEW
// ============================================================
function DossieView({ pessoa, dossie, onFechar }: { pessoa: PessoaLista; dossie: Dossie; onFechar: () => void }) {
  const qc = useQueryClient()
  const c = dossie.credito
  const rc = dossie.resumo_comportamento
  const ai = dossie.analise_ia
  const [tab, setTab] = useState<TabId>('titulos')
  const [modalLimite, setModalLimite] = useState(false)
  const [modalNivel, setModalNivel] = useState(false)
  const [modalBloquear, setModalBloquear] = useState(false)
  const [modalObs, setModalObs] = useState(false)
  const [modalUpload, setModalUpload] = useState(false)

  const invalidateDossie = () => qc.invalidateQueries({ queryKey: ['dossie', pessoa.id] })

  const corUso = c.percentual_usado === null
    ? 'bg-ink-100 text-ink-700'
    : c.percentual_usado < 50 ? 'bg-emerald-100 text-emerald-700'
    : c.percentual_usado < 80 ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700'

  const corPontualidade = rc.pontualidade_pct === null
    ? 'bg-ink-100 text-ink-600'
    : rc.pontualidade_pct >= 90 ? 'bg-emerald-100 text-emerald-700'
    : rc.pontualidade_pct >= 70 ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700'

  return (
    <div className="space-y-4">
      {/* Header cliente + CTA Habilitar Crédito */}
      <div className="card card-body flex items-start gap-4">
        <div className="bg-brand-100 rounded-full p-3">
          <User size={24} className="text-brand-700" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold">{pessoa.nome}</h2>
            {pessoa.codigo && <span className="text-xs bg-ink-100 text-ink-700 px-2 py-0.5 rounded font-mono">{pessoa.codigo}</span>}
            {c.bloqueado && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded flex items-center gap-1"><XCircle size={12} /> Crédito bloqueado</span>}
          </div>
          <div className="text-sm text-ink-600 mt-1 flex items-center gap-3 flex-wrap">
            {pessoa.documento && <span>{pessoa.documento}</span>}
            {pessoa.email && <span>· {pessoa.email}</span>}
            {pessoa.telefone && <span>· {pessoa.telefone}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {!c.limite_configurado && !c.bloqueado && (
            <button onClick={() => setModalLimite(true)} className="btn-primary">
              <ShieldCheck size={16} /> Habilitar Crédito
            </button>
          )}
          {c.limite_configurado && (
            <button onClick={() => setModalLimite(true)} className="btn-ghost text-sm">
              <Wallet size={14} /> Alterar limite
            </button>
          )}
          <button onClick={() => setModalNivel(true)} className="btn-ghost text-sm">
            <CreditCard size={14} /> Nível: {({ avista: 'À vista', cheque: 'Cheque', boleto: 'Boleto', a_prazo: 'A prazo' } as any)[c.nivel_cobranca] || 'À vista'}
          </button>
          <button onClick={() => setModalBloquear(true)}
            className={`btn-ghost text-sm ${c.bloqueado ? 'text-emerald-700 hover:bg-emerald-50' : 'text-red-700 hover:bg-red-50'}`}>
            {c.bloqueado ? <><Unlock size={14} /> Desbloquear</> : <><Lock size={14} /> Bloquear</>}
          </button>
          <button onClick={() => setModalObs(true)} className="btn-ghost text-sm">
            <MessageSquare size={14} /> Observações
          </button>
          <BotaoConsultarBureau provider="serasa_experian" label="Serasa" pessoaId={pessoa.id} />
          <BotaoConsultarBureau provider="spc_brasil" label="SPC" pessoaId={pessoa.id} />
          <button onClick={onFechar} className="btn-ghost text-sm">Fechar</button>
        </div>
      </div>

      {/* Análise IA */}
      <CardAnaliseIA ai={ai} />

      {/* 4 cards de resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <CardResumo titulo="Limite"
          valor={c.limite_configurado ? formatMoney(c.limite || 0) : 'Não configurado'}
          sub={c.limite_configurado ? null : 'Configure pra habilitar venda a prazo'}
          destaque={c.limite_configurado ? 'text-brand-700' : 'text-red-600'} />
        <CardResumo titulo="Saldo Devedor"
          valor={formatMoney(c.saldo_devedor)}
          sub={`${rc.titulos_em_aberto} título(s) em aberto`}
          destaque={c.saldo_devedor > 0 ? 'text-amber-700' : 'text-emerald-700'} />
        <CardResumo titulo="Disponível"
          valor={c.disponivel !== null ? formatMoney(c.disponivel) : '—'}
          sub={c.percentual_usado !== null ? `${c.percentual_usado}% usado` : 'sem limite'}
          destaque={corUso} tipoDestaque="pill" />
        <CardResumo titulo="Pontualidade"
          valor={rc.pontualidade_pct !== null ? `${rc.pontualidade_pct}%` : 'Sem histórico'}
          sub={
            rc.titulos_em_atraso_agora > 0 ? `${rc.titulos_em_atraso_agora} em atraso agora`
            : rc.atraso_medio_dias > 0 ? `atraso médio: ${rc.atraso_medio_dias}d`
            : 'tudo em dia'
          }
          destaque={corPontualidade} tipoDestaque="pill" />
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-ink-200 flex gap-1 px-2 overflow-x-auto">
          <TabBtn active={tab === 'titulos'} onClick={() => setTab('titulos')} icon={ListChecks} label={`Títulos em aberto (${dossie.titulos_em_aberto.length})`} />
          <TabBtn active={tab === 'pedidos'} onClick={() => setTab('pedidos')} icon={Receipt} label={`Pedidos (${dossie.ultimos_pedidos.length})`} />
          <TabBtn active={tab === 'pagamentos'} onClick={() => setTab('pagamentos')} icon={CheckCircle2} label={`Pagamentos (${dossie.ultimos_pagamentos.length})`} />
          <TabBtn active={tab === 'documentos'} onClick={() => setTab('documentos')} icon={FileText} label="Documentos" />
          <TabBtn active={tab === 'historico'} onClick={() => setTab('historico')} icon={ClipboardList} label="Histórico de alterações" />
        </div>
        <div className="p-4">
          {tab === 'titulos' && <TabTitulos itens={dossie.titulos_em_aberto} />}
          {tab === 'pedidos' && <TabPedidos itens={dossie.ultimos_pedidos} />}
          {tab === 'pagamentos' && <TabPagamentos itens={dossie.ultimos_pagamentos} />}
          {tab === 'documentos' && <TabDocumentos pessoa_id={pessoa.id} onUpload={() => setModalUpload(true)} />}
          {tab === 'historico' && <TabHistorico pessoa_id={pessoa.id} />}
        </div>
      </div>

      {modalLimite && (
        <ModalLimite
          pessoa_id={pessoa.id}
          limite_atual={c.limite}
          recomendacao={ai.recomendacao_limite}
          onClose={() => setModalLimite(false)}
          onOk={() => { setModalLimite(false); invalidateDossie() }}
        />
      )}
      {modalBloquear && (
        <ModalBloquear
          pessoa_id={pessoa.id}
          bloqueado_atual={c.bloqueado}
          onClose={() => setModalBloquear(false)}
          onOk={() => { setModalBloquear(false); invalidateDossie() }}
        />
      )}
      {modalNivel && (
        <ModalNivel
          pessoa_id={pessoa.id}
          nivel_atual={c.nivel_cobranca}
          condicao_atual={c.condicao_pagamento_id}
          onClose={() => setModalNivel(false)}
          onOk={() => { setModalNivel(false); invalidateDossie() }}
        />
      )}
      {modalObs && (
        <ModalObservacoes
          pessoa_id={pessoa.id}
          obs_atual={c.observacoes_financeiras}
          onClose={() => setModalObs(false)}
          onOk={() => { setModalObs(false); invalidateDossie() }}
        />
      )}
      {modalUpload && (
        <ModalUploadDoc
          pessoa_id={pessoa.id}
          onClose={() => setModalUpload(false)}
          onOk={() => { setModalUpload(false); invalidateDossie() }}
        />
      )}
    </div>
  )
}

// ============================================================
// CARD ANALISE IA
// ============================================================
function CardAnaliseIA({ ai }: { ai: AnaliseIA }) {
  const meta = {
    baixo: { cor: 'bg-emerald-50 border-emerald-300 text-emerald-800', label: '🟢 Baixo risco', Icon: ShieldCheck },
    moderado: { cor: 'bg-amber-50 border-amber-300 text-amber-800', label: '🟡 Risco moderado', Icon: ShieldAlert },
    alto: { cor: 'bg-red-50 border-red-300 text-red-800', label: '🔴 Alto risco', Icon: AlertTriangle },
    sem_historico: { cor: 'bg-ink-50 border-ink-300 text-ink-700', label: '⚪ Sem histórico', Icon: ShieldQuestion },
  }[ai.nivel_risco]

  return (
    <div className={`card card-body border-2 ${meta.cor}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0">
          <Sparkles size={20} className="text-brand-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <meta.Icon size={16} />
            <span className="font-semibold">{meta.label}</span>
            <span className="text-xs bg-white/60 px-2 py-0.5 rounded">Análise da Sofia</span>
            <span className="text-xs text-ink-500">({ai.metodo})</span>
          </div>
          <p className="text-sm mt-2">{ai.resumo}</p>
          <div className="text-xs mt-2 flex gap-4 flex-wrap">
            {ai.ticket_medio > 0 && <span>Ticket médio: <b>R$ {ai.ticket_medio.toFixed(2)}</b></span>}
            {ai.recomendacao_limite > 0 && <span>Limite sugerido: <b>R$ {ai.recomendacao_limite.toFixed(2)}</b></span>}
          </div>
          {ai.alertas.length > 0 && (
            <div className="mt-3 space-y-1">
              <div className="text-xs font-semibold uppercase opacity-70">Alertas</div>
              {ai.alertas.map((a, i) => (
                <div key={i} className="text-sm flex gap-2 items-start">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{a}</span>
                </div>
              ))}
            </div>
          )}
          {ai.acoes_sugeridas.length > 0 && (
            <div className="mt-3 space-y-1">
              <div className="text-xs font-semibold uppercase opacity-70">Ações sugeridas</div>
              {ai.acoes_sugeridas.map((a, i) => (
                <div key={i} className="text-sm flex gap-2 items-start">
                  <TrendingUp size={14} className="mt-0.5 shrink-0" /> <span>{a}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================
// TABS
// ============================================================
function TabBtn({ active, onClick, icon: Icon, label }: any) {
  return (
    <button onClick={onClick}
      className={`px-3 py-2 text-sm flex items-center gap-1.5 border-b-2 whitespace-nowrap transition ${
        active ? 'border-brand-600 text-brand-700 font-medium' : 'border-transparent text-ink-600 hover:text-ink-900'
      }`}>
      <Icon size={14} /> {label}
    </button>
  )
}

function TabTitulos({ itens }: { itens: any[] }) {
  if (itens.length === 0) return <Empty texto="Nenhum título em aberto." />
  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead className="text-xs text-ink-500 uppercase border-b">
          <tr>
            <th className="text-left py-2">Descrição</th>
            <th className="text-left">Parcela</th>
            <th className="text-left">Vencimento</th>
            <th className="text-right">Valor</th>
            <th className="text-right">Pago</th>
            <th className="text-right">Saldo</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {itens.map(t => (
            <tr key={t.id}>
              <td className="py-2">{t.descricao}</td>
              <td>{t.parcela}/{t.total_parcelas}</td>
              <td>
                {formatDate(t.vencimento)}{' '}
                {t.atrasado
                  ? <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded ml-1">atrasado {Math.abs(t.dias_para_vencer)}d</span>
                  : t.dias_para_vencer !== null && t.dias_para_vencer <= 7
                  ? <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded ml-1">vence em {t.dias_para_vencer}d</span>
                  : null}
              </td>
              <td className="text-right">{formatMoney(t.valor)}</td>
              <td className="text-right text-ink-500">{formatMoney(t.valor_pago)}</td>
              <td className="text-right font-medium">{formatMoney(t.saldo)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TabPedidos({ itens }: { itens: any[] }) {
  if (itens.length === 0) return <Empty texto="Nenhum pedido encontrado." />
  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead className="text-xs text-ink-500 uppercase border-b">
          <tr>
            <th className="text-left py-2">Pedido</th>
            <th className="text-left">Data</th>
            <th className="text-left">Status</th>
            <th className="text-left">Pagamento</th>
            <th className="text-right">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {itens.map(p => (
            <tr key={p.id}>
              <td className="py-2 font-mono">#{p.numero}</td>
              <td>{formatDateTime(p.criado_em)}</td>
              <td><span className="text-xs bg-ink-100 text-ink-700 px-2 py-0.5 rounded">{p.status}</span></td>
              <td className="capitalize">{(p.forma_pagamento || '-').replace('_', ' ')} · {p.parcelas}x</td>
              <td className="text-right font-medium">{formatMoney(p.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TabPagamentos({ itens }: { itens: any[] }) {
  if (itens.length === 0) return <Empty texto="Nenhum pagamento registrado." />
  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead className="text-xs text-ink-500 uppercase border-b">
          <tr>
            <th className="text-left py-2">Descrição</th>
            <th className="text-left">Vencimento</th>
            <th className="text-left">Pago em</th>
            <th className="text-left">Atraso</th>
            <th className="text-right">Valor</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {itens.map(t => (
            <tr key={t.id}>
              <td className="py-2">{t.descricao}</td>
              <td>{formatDate(t.vencimento)}</td>
              <td>{t.pago_em ? formatDate(t.pago_em) : '-'}</td>
              <td>
                {t.dias_atraso === null ? '-' :
                 t.dias_atraso <= 0 ? <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">no prazo</span> :
                 <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">+{t.dias_atraso}d</span>}
              </td>
              <td className="text-right">{formatMoney(t.valor)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TabDocumentos({ pessoa_id, onUpload }: { pessoa_id: number; onUpload: () => void }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<any>({
    queryKey: ['docs', pessoa_id],
    queryFn: async () => (await api.get(`/financeiro/analise-credito/${pessoa_id}/documentos`)).data,
  })

  async function excluir(doc_id: number, nome: string) {
    try {
      await api.delete(`/financeiro/analise-credito/${pessoa_id}/documentos/${doc_id}`)
      toast.success('Documento excluído')
      qc.invalidateQueries({ queryKey: ['docs', pessoa_id] })
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao excluir')
    }
  }

  if (isLoading) return <div className="text-center py-6 text-ink-500 text-sm"><Loader2 size={18} className="mx-auto animate-spin" /></div>

  const temDocs = data && data.total_documentos > 0

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-ink-500">
          {temDocs
            ? <>{data.total_documentos} documento(s) · {data.total_mb} MB usados de {data.quota_mb} MB ({data.quota_usada_pct}%)</>
            : <>Nenhum documento anexado.</>}
        </div>
        <button onClick={onUpload} className="btn-primary text-sm">
          <Plus size={14} /> Anexar documento
        </button>
      </div>
      {!temDocs && <Empty texto="Use o botão acima para anexar o primeiro documento (PDF, JPG, PNG, WEBP até 20MB)." />}
      {temDocs && (
        <div className="divide-y border border-ink-200 rounded">
          {data.itens.map((d: any) => (
            <div key={d.id} className="flex items-center gap-3 px-3 py-2">
              <FileText size={16} className="text-ink-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{d.descricao || d.tipo}</div>
                <div className="text-xs text-ink-500">
                  {d.tipo} · {d.tamanho_mb} MB · {formatDateTime(d.criado_em)}
                  {d.criado_por_nome && ` · ${d.criado_por_nome}`}
                </div>
              </div>
              <a href={d.url} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs">
                Abrir
              </a>
              <button onClick={() => pedirExclusao({ descricao: `o documento "${d.descricao || d.tipo}"`, onConfirm: () => excluir(d.id, d.descricao || d.tipo) })} className="text-ink-400 hover:text-red-600" title="Excluir (admin)">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function TabHistorico({ pessoa_id }: { pessoa_id: number }) {
  const { data = [], isLoading } = useQuery<any[]>({
    queryKey: ['logs-credito', pessoa_id],
    queryFn: async () => (await api.get(`/financeiro/analise-credito/${pessoa_id}/logs`)).data,
  })
  if (isLoading) return <div className="text-center py-6 text-ink-500 text-sm"><Loader2 size={18} className="mx-auto animate-spin" /></div>
  if (data.length === 0) return <Empty texto="Nenhuma alteração registrada ainda." />
  return (
    <div className="space-y-2">
      {data.map(l => (
        <div key={l.id} className="border border-ink-200 rounded p-3 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs bg-ink-100 text-ink-700 px-2 py-0.5 rounded uppercase">{l.tipo}</span>
            <span className="text-xs text-ink-500">{formatDateTime(l.criado_em)}</span>
            {l.usuario_nome && <span className="text-xs text-ink-500">por {l.usuario_nome}</span>}
          </div>
          <div className="mt-1 text-ink-700">
            <span className="text-ink-500">{l.valor_antes || '—'}</span>
            {' → '}
            <span className="font-medium">{l.valor_depois || '—'}</span>
          </div>
          {l.justificativa && <div className="mt-1 text-ink-600 italic">"{l.justificativa}"</div>}
        </div>
      ))}
    </div>
  )
}

function Empty({ texto }: { texto: string }) {
  return <div className="text-center py-6 text-ink-500 text-sm">{texto}</div>
}

function CardResumo({ titulo, valor, sub, destaque, tipoDestaque = 'text' }: any) {
  const cls = tipoDestaque === 'pill'
    ? `inline-block px-2 py-0.5 rounded text-sm font-semibold ${destaque || ''}`
    : `text-xl font-bold ${destaque || 'text-ink-900'}`
  return (
    <div className="card card-body">
      <div className="text-xs text-ink-500 uppercase tracking-wide">{titulo}</div>
      <div className={`mt-1 ${cls}`}>{valor}</div>
      {sub && <div className="text-xs text-ink-500 mt-1">{sub}</div>}
    </div>
  )
}

// ============================================================
// MODAL DE LIMITE (alterar/habilitar)
// ============================================================
function ModalLimite({
  pessoa_id, limite_atual, recomendacao, onClose, onOk,
}: {
  pessoa_id: number
  limite_atual: number | null
  recomendacao: number
  onClose: () => void
  onOk: () => void
}) {
  const ehHabilitar = limite_atual === null
  const [valor, setValor] = useState<string>(
    limite_atual !== null ? String(limite_atual) : (recomendacao > 0 ? String(recomendacao) : '')
  )
  const [justificativa, setJustificativa] = useState('')
  const [loading, setLoading] = useState(false)

  async function salvar() {
    const v = parseFloat(valor)
    if (isNaN(v) || v < 0) {
      toast.error('Valor inválido')
      return
    }
    if (justificativa.trim().length < 10) {
      toast.error('Justificativa precisa de no mínimo 10 caracteres')
      return
    }
    setLoading(true)
    try {
      await api.put(`/financeiro/analise-credito/${pessoa_id}/limite`, {
        valor: v,
        justificativa: justificativa.trim(),
      })
      toast.success(ehHabilitar ? 'Crédito habilitado' : 'Limite alterado')
      onOk()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            {ehHabilitar ? <><ShieldCheck size={18} className="text-emerald-600" /> Habilitar Crédito</> : <><Wallet size={18} /> Alterar Limite</>}
          </h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><XCircle size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {ehHabilitar && recomendacao > 0 && (
            <div className="bg-brand-50 border border-brand-200 rounded p-3 text-sm">
              <div className="flex items-center gap-1.5 text-brand-700 font-medium mb-1">
                <Sparkles size={14} /> Sugestão da Sofia
              </div>
              <div className="text-ink-700">
                Limite recomendado: <b>R$ {recomendacao.toFixed(2)}</b> (baseado em histórico)
              </div>
            </div>
          )}
          <div>
            <label className="label">Valor do limite (R$)</label>
            <input type="number" min={0} step={0.01} className="input"
              value={valor} onChange={e => setValor(e.target.value)} autoFocus />
          </div>
          <div>
            <label className="label">Justificativa (mín. 10 chars)</label>
            <textarea className="input" rows={3}
              value={justificativa} onChange={e => setJustificativa(e.target.value)}
              placeholder="Ex: Cliente antigo com bom histórico, avaliação em 18/04" />
            <div className="text-xs text-ink-500 mt-1">{justificativa.length}/1000</div>
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-ink-50 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-sm">Cancelar</button>
          <button onClick={salvar} disabled={loading} className="btn-primary">
            {loading ? <><Loader2 size={14} className="animate-spin" /> Salvando...</> : (ehHabilitar ? 'Habilitar' : 'Salvar')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// MODAL NÍVEL DE COBRANÇA (forma + prazo permitidos)
// ============================================================
function ModalNivel({
  pessoa_id, nivel_atual, condicao_atual, onClose, onOk,
}: { pessoa_id: number; nivel_atual: string; condicao_atual: number | null; onClose: () => void; onOk: () => void }) {
  const [nivel, setNivel] = useState(nivel_atual || 'avista')
  const [condId, setCondId] = useState<number | ''>(condicao_atual || '')
  const [just, setJust] = useState('')
  // prazos de formas a prazo (boleto, etc) — todas as condições cadastradas
  const { data: conds = [] } = useQuery<any[]>({
    queryKey: ['cond-nivel'],
    queryFn: async () => (await api.get('/caixa/condicoes-pagamento')).data,
  })
  const exigePrazo = nivel === 'boleto' || nivel === 'a_prazo'
  const salvar = useMutation({
    mutationFn: async () => (await api.put(`/financeiro/analise-credito/${pessoa_id}/nivel-cobranca`, {
      nivel_cobranca: nivel,
      condicao_pagamento_id: exigePrazo ? (condId || null) : null,
      justificativa: just,
    })).data,
    onSuccess: () => { toast.success('Nível de cobrança atualizado'); onOk() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const podeServar = just.trim().length >= 10 && (!exigePrazo || condId)
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2"><CreditCard size={18} /> Nível de cobrança</h2>
          <button onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <p className="text-xs text-ink-500">Nível = teto de cobrança (cumulativo): o cliente pode usar o nível dele <em>e todos os de baixo</em>. Ordem: <strong>À vista &lt; Cheque &lt; Boleto &lt; A prazo</strong>.</p>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setNivel('avista')}
              className={`p-3 rounded-lg border text-left ${nivel === 'avista' ? 'border-brand-500 bg-brand-50' : 'border-ink-200'}`}>
              <div className="font-semibold">À vista</div>
              <div className="text-xs text-ink-500">pix · dinheiro · cartão</div>
            </button>
            <button onClick={() => setNivel('cheque')}
              className={`p-3 rounded-lg border text-left ${nivel === 'cheque' ? 'border-brand-500 bg-brand-50' : 'border-ink-200'}`}>
              <div className="font-semibold">Cheque</div>
              <div className="text-xs text-ink-500">cheque + à vista</div>
            </button>
            <button onClick={() => setNivel('boleto')}
              className={`p-3 rounded-lg border text-left ${nivel === 'boleto' ? 'border-brand-500 bg-brand-50' : 'border-ink-200'}`}>
              <div className="font-semibold">Boleto</div>
              <div className="text-xs text-ink-500">boleto + cheque + à vista</div>
            </button>
            <button onClick={() => setNivel('a_prazo')}
              className={`p-3 rounded-lg border text-left ${nivel === 'a_prazo' ? 'border-brand-500 bg-brand-50' : 'border-ink-200'}`}>
              <div className="font-semibold">A prazo (carteira)</div>
              <div className="text-xs text-ink-500">qualquer forma</div>
            </button>
          </div>
          {exigePrazo && (
            <label className="block">
              <div className="text-xs font-medium mb-1">Prazo amarrado *</div>
              <select className="input w-full" value={condId} onChange={(e) => setCondId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">— escolha o prazo —</option>
                {conds.map((c: any) => <option key={c.id} value={c.id}>{c.forma} · {c.nome} ({c.parcelas}x: {c.dias.join('/')}d)</option>)}
              </select>
            </label>
          )}
          <label className="block">
            <div className="text-xs font-medium mb-1">Justificativa * (mín. 10 caracteres)</div>
            <textarea className="input w-full" rows={2} value={just} onChange={(e) => setJust(e.target.value)} placeholder="Ex: cliente aprovado p/ boleto 30/60" />
          </label>
          <div className="flex gap-2 pt-1">
            <button className="btn-ghost flex-1" onClick={onClose}>Cancelar</button>
            <button className="btn-primary flex-1" disabled={!podeServar || salvar.isPending} onClick={() => salvar.mutate()}>
              {salvar.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Salvar nível'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// MODAL DE BLOQUEIO (bloquear/desbloquear credito)
// ============================================================
function ModalBloquear({
  pessoa_id, bloqueado_atual, onClose, onOk,
}: {
  pessoa_id: number
  bloqueado_atual: boolean
  onClose: () => void
  onOk: () => void
}) {
  const [justificativa, setJustificativa] = useState('')
  const [loading, setLoading] = useState(false)
  const novoEstado = !bloqueado_atual

  async function salvar() {
    if (justificativa.trim().length < 10) {
      toast.error('Justificativa precisa de no mínimo 10 caracteres')
      return
    }
    setLoading(true)
    try {
      await api.put(`/financeiro/analise-credito/${pessoa_id}/bloquear`, {
        bloqueado: novoEstado,
        justificativa: justificativa.trim(),
      })
      toast.success(novoEstado ? 'Crédito bloqueado' : 'Crédito desbloqueado')
      onOk()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            {novoEstado
              ? <><Lock size={18} className="text-red-600" /> Bloquear Crédito</>
              : <><Unlock size={18} className="text-emerald-600" /> Desbloquear Crédito</>}
          </h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><XCircle size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className={`border rounded p-3 text-sm ${novoEstado ? 'bg-red-50 border-red-200 text-red-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
            {novoEstado
              ? 'Vendas a prazo ficarão bloqueadas para este cliente, independentemente do limite configurado.'
              : 'Vendas a prazo voltarão a respeitar o limite configurado.'}
          </div>
          <div>
            <label className="label">Justificativa (mín. 10 chars)</label>
            <textarea className="input" rows={3}
              value={justificativa} onChange={e => setJustificativa(e.target.value)}
              placeholder={novoEstado
                ? 'Ex: Inadimplência recorrente, aguardando regularização'
                : 'Ex: Cliente regularizou pagamentos, pode voltar a comprar a prazo'} />
            <div className="text-xs text-ink-500 mt-1">{justificativa.length}/1000</div>
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-ink-50 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-sm">Cancelar</button>
          <button onClick={salvar} disabled={loading}
            className={novoEstado ? 'btn-primary !bg-red-600 hover:!bg-red-700' : 'btn-primary !bg-emerald-600 hover:!bg-emerald-700'}>
            {loading ? <><Loader2 size={14} className="animate-spin" /> Salvando...</> : (novoEstado ? 'Bloquear' : 'Desbloquear')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// MODAL DE OBSERVACOES FINANCEIRAS (texto livre)
// ============================================================
function ModalObservacoes({
  pessoa_id, obs_atual, onClose, onOk,
}: {
  pessoa_id: number
  obs_atual: string | null
  onClose: () => void
  onOk: () => void
}) {
  const [texto, setTexto] = useState(obs_atual || '')
  const [loading, setLoading] = useState(false)

  async function salvar() {
    setLoading(true)
    try {
      await api.put(`/financeiro/analise-credito/${pessoa_id}/observacoes`, {
        observacoes: texto.trim() || null,
      })
      toast.success('Observações salvas')
      onOk()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <MessageSquare size={18} /> Observações Financeiras
          </h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><XCircle size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-ink-500">
            Anotações internas sobre o comportamento financeiro do cliente.
            Visível para equipe de vendas e financeiro.
          </p>
          <textarea className="input" rows={6}
            value={texto} onChange={e => setTexto(e.target.value)}
            placeholder="Ex: Cliente antigo, sempre paga em dia. Dono Pedro prefere faturar até dia 20. Contato: João (gerente)." />
          <div className="text-xs text-ink-500">{texto.length} caractere(s)</div>
        </div>
        <div className="px-5 py-3 border-t bg-ink-50 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-sm">Cancelar</button>
          <button onClick={salvar} disabled={loading} className="btn-primary">
            {loading ? <><Loader2 size={14} className="animate-spin" /> Salvando...</> : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// MODAL DE UPLOAD DE DOCUMENTO
// ============================================================
const TIPOS_DOC = [
  { valor: 'rg', label: 'RG' },
  { valor: 'cpf', label: 'CPF' },
  { valor: 'cnpj', label: 'CNPJ' },
  { valor: 'comprovante_residencia', label: 'Comprovante de residência' },
  { valor: 'comprovante_renda', label: 'Comprovante de renda' },
  { valor: 'contrato', label: 'Contrato' },
  { valor: 'ficha_cadastral', label: 'Ficha cadastral' },
  { valor: 'outro', label: 'Outro' },
]

function ModalUploadDoc({
  pessoa_id, onClose, onOk,
}: {
  pessoa_id: number
  onClose: () => void
  onOk: () => void
}) {
  const [tipo, setTipo] = useState('outro')
  const [descricao, setDescricao] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)

  async function enviar() {
    if (!file) {
      toast.error('Selecione um arquivo')
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Arquivo excede 20 MB')
      return
    }
    setLoading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('tipo', tipo)
      if (descricao.trim()) form.append('descricao', descricao.trim())

      await api.post(`/financeiro/analise-credito/${pessoa_id}/documentos`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      toast.success('Documento anexado')
      onOk()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao enviar documento')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Upload size={18} /> Anexar Documento
          </h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><XCircle size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="label">Tipo</label>
            <select className="input" value={tipo} onChange={e => setTipo(e.target.value)}>
              {TIPOS_DOC.map(t => <option key={t.valor} value={t.valor}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Descrição (opcional)</label>
            <input type="text" className="input" maxLength={200}
              value={descricao} onChange={e => setDescricao(e.target.value)}
              placeholder="Ex: RG frente+verso atualizado 2026" />
          </div>
          <div>
            <label className="label">Arquivo (PDF, JPG, PNG, WEBP — máx 20 MB)</label>
            <input type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-ink-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-ink-200 file:bg-white file:text-sm file:font-medium hover:file:bg-ink-50" />
            {file && (
              <div className="text-xs text-ink-500 mt-1">
                {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
              </div>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-ink-50 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-sm">Cancelar</button>
          <button onClick={enviar} disabled={loading || !file} className="btn-primary">
            {loading ? <><Loader2 size={14} className="animate-spin" /> Enviando...</> : 'Anexar'}
          </button>
        </div>
      </div>
    </div>
  )
}


// ============================================================
// BOTAO CONSULTAR BUREAU — Serasa Experian OU SPC Brasil
// ============================================================
function BotaoConsultarBureau({ provider, label, pessoaId }: {
  provider: 'serasa_experian' | 'spc_brasil'
  label: string
  pessoaId: number
}) {
  const { data: status } = useQuery<any>({
    queryKey: ['integracao-bureau-status', provider],
    queryFn: async () =>
      (await api.get(`/financeiro/analise-credito/integracoes/${provider}`)).data,
    staleTime: 30_000,
  })
  const qc = useQueryClient()

  const consultar = useMutation({
    mutationFn: async () =>
      (await api.post(`/financeiro/analise-credito/${pessoaId}/consultar/${provider}`)).data,
    onSuccess: (d: any) => {
      if (d?.status === 'sucesso') {
        toast.success(`Consulta ${label} OK — score ${d.score ?? '—'}`)
      } else {
        toast.success(`Consulta ${label} enviada`)
      }
      qc.invalidateQueries({ queryKey: ['dossie', pessoaId] })
    },
    onError: (e: any) => {
      const detail = e?.response?.data?.detail
      const code = detail?.code || ''
      const msg = detail?.message || `Erro na consulta ${label}`
      if (code === 'MODULO_CONSULTA_EM_DESENVOLVIMENTO') {
        toast(`${msg}`, { icon: '🛠️', duration: 5000 })
      } else if (code.includes('NAO_CONFIGURADA')) {
        toast.error(msg, { duration: 6000 })
      } else if (code.includes('INATIVA')) {
        toast.error(msg, { duration: 6000 })
      } else {
        toast.error(typeof msg === 'string' ? msg : `Erro na consulta ${label}`)
      }
    },
  })

  let tooltip = `Consultar ${label}`
  let cor = 'text-brand-700'
  let disabled = false
  if (status) {
    if (!status.configurado) {
      tooltip = `Configure credenciais em Análise de Crédito → Bureaus de Crédito`
      cor = 'text-ink-400'
      disabled = true
    } else if (!status.ativo) {
      tooltip = `Integração ${label} está inativa — ative em Bureaus de Crédito`
      cor = 'text-amber-600'
      disabled = true
    }
  } else {
    disabled = true
  }

  return (
    <button
      type="button"
      onClick={() => consultar.mutate()}
      disabled={disabled || consultar.isPending}
      title={tooltip}
      className={`btn-ghost text-sm ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${cor}`}
    >
      {consultar.isPending ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
      Consultar {label}
    </button>
  )
}
